import { useState } from 'react';

interface ShareOptions {
  url?: string;
  text?: string;
  title?: string;
}

interface UseShareReturn {
  shareOnWhatsApp: (phoneNumber?: string, message?: string) => void;
  shareViaNativeAPI: (options: ShareOptions) => Promise<void>;
  copyToClipboard: (text: string) => Promise<boolean>;
  isSupported: boolean;
  isSharing: boolean;
}

export const useShare = (): UseShareReturn => {
  const [isSharing, setIsSharing] = useState(false);

  // Check if Web Share API is supported
  const isSupported = typeof navigator !== 'undefined' && 'share' in navigator;

  // Share on WhatsApp
  const shareOnWhatsApp = (phoneNumber?: string, message?: string) => {
    const encodedMessage = encodeURIComponent(message || '');
    let whatsappUrl = '';

    if (phoneNumber) {
      // Share to specific phone number
      const cleanPhoneNumber = phoneNumber.replace(/[^\d]/g, ''); // Remove non-numeric characters
      whatsappUrl = `https://wa.me/${cleanPhoneNumber}?text=${encodedMessage}`;
    } else {
      // Open WhatsApp with message (user can choose contact)
      whatsappUrl = `https://api.whatsapp.com/send?text=${encodedMessage}`;
    }

    window.open(whatsappUrl, '_blank');
  };

  // Share via native Web Share API
  const shareViaNativeAPI = async (options: ShareOptions): Promise<void> => {
    if (!isSupported) {
      throw new Error('Web Share API is not supported in this browser');
    }

    setIsSharing(true);
    try {
      await navigator.share({
        title: options.title,
        text: options.text,
        url: options.url,
      });
    } catch (error) {
      // User cancelled or error occurred
      if ((error as Error).name !== 'AbortError') {
        throw error;
      }
    } finally {
      setIsSharing(false);
    }
  };

  // Copy text to clipboard
  const copyToClipboard = async (text: string): Promise<boolean> => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'absolute';
        textArea.style.left = '-999999px';
        document.body.prepend(textArea);
        textArea.select();
        const success = document.execCommand('copy');
        textArea.remove();
        return success;
      }
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      return false;
    }
  };

  return {
    shareOnWhatsApp,
    shareViaNativeAPI,
    copyToClipboard,
    isSupported,
    isSharing,
  };
};